import React, { PropsWithChildren, ReactNode } from "react";
import Head from "next/head";
import {
  Form,
  Formik,
  FormikConfig,
  FormikProps as FormikPropsLib,
  FormikValues,
} from "formik";
import {
  ColorsKey,
  Footer,
  colors,
  layout,
} from "@literati/public-ui-shared-react";
import StepFooter, {
  FOOTER_HEIGHT,
  StepFooterClassName,
  StepFooterProps,
} from "components/Step/StepFooter";
import StepHeader, {
  HEADER_HEIGHT,
  StepHeaderClassName,
  StepHeaderProps,
} from "components/Step/StepHeader";
import styled from "styled-components";
import { primaryHeading } from "components/Step/stepStyles";
import { DLSErrorMessageClassName } from "components/DLS/DLSErrorMessage";

const CLASS_BASE = "step-layout";
export const StepLayoutClassName = {
  BASE: CLASS_BASE,
  STEP: `${CLASS_BASE}-page`,
  CONTENT_SPACING: `${CLASS_BASE}-content-spacing`,
  HEADING: `${CLASS_BASE}-heading`,
  SUBHEADING: `${CLASS_BASE}-subheading`,
  FORM: `${CLASS_BASE}-form`,
  NON_FORM_CONTAINER: `${CLASS_BASE}-non-form-container`,
} as const;

const Page = styled.section<{ $bgColor: ColorsKey }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100dvh;
  width: 100vw;
  background: ${({ $bgColor }) => colors[$bgColor]};

  .${StepHeaderClassName.BASE} {
    flex: 0 0 ${HEADER_HEIGHT}px;
  }

  .${StepFooterClassName.BASE} {
    flex: 0 0 ${FOOTER_HEIGHT}px;
  }
`;

type ContentSpacingArgs = {
  $bgColor: ColorsKey;
  $disableContentSpacing: boolean;
  $footerVariant: FooterVariant;
};

const ContentSpacing = styled.div<ContentSpacingArgs>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${({ $footerVariant }) =>
    $footerVariant === "full" ? "100vw" : "unset"};
  overflow-y: ${({ $footerVariant }) =>
    $footerVariant === "sticky" ? "scroll" : "unset"};
  background: ${({ $bgColor }) => colors[$bgColor]};

  ${({ $disableContentSpacing }) =>
    !$disableContentSpacing && layout.contentSpacing};
`;

const StyledForm = styled(Form)`
  max-width: 21rem;
  width: 100%;

  .${DLSErrorMessageClassName.BASE} {
    align-self: flex-start;
  }
`;

const PrimaryHeading = styled.h1`
  ${primaryHeading};
  margin: 2rem 0 0;
`;

const Subheading = styled.p`
  margin: 0.75rem 0 0;
  color: ${colors.inkBlack};
  font-size: 0.875rem;
  text-align: center;
`;

export type FooterVariant = "sticky" | "full";

export type FormikProps<Values extends FormikValues> = FormikConfig<Values>;

export type FormikChildrenRenderProps<Values extends FormikValues> = Omit<
  FormikPropsLib<Values>,
  "onSubmit"
>;

export type StepLayoutProps<Values extends FormikValues> =
  | {
      appendedContent?: ReactNode;
      asForm: true;
      bgColor?: ColorsKey;
      children:
        | ReactNode
        | ((props: FormikChildrenRenderProps<Values>) => void);
      className?: string;
      disableContentSpacing?: boolean;
      footerVariant?: FooterVariant;
      formId: string;
      formikProps: FormikProps<Values>;
      heading?: ReactNode;
      pageTitle?: string;
      prependedContent?: ReactNode;
      stepFooterProps?: StepFooterProps;
      stepHeaderProps?: StepHeaderProps;
      suppressFooter?: boolean;
      subheading?: ReactNode;
    }
  | {
      appendedContent?: ReactNode;
      asForm: false;
      bgColor?: ColorsKey;
      children: ReactNode;
      className?: string;
      disableContentSpacing?: boolean;
      footerVariant?: FooterVariant;
      heading?: ReactNode;
      pageTitle?: string;
      prependedContent?: ReactNode;
      stepFooterProps?: StepFooterProps;
      stepHeaderProps?: StepHeaderProps;
      suppressFooter?: boolean;
      subheading?: ReactNode;
    };

export function StepLayout<Values extends FormikValues>(
  props: PropsWithChildren<StepLayoutProps<Values>>
) {
  const cls = StepLayoutClassName;

  const {
    appendedContent = null,
    asForm,
    bgColor = "transparent",
    children,
    className,
    disableContentSpacing = false,
    footerVariant = "sticky",
    heading,
    pageTitle,
    prependedContent = null,
    stepFooterProps,
    stepHeaderProps,
    suppressFooter = false,
    subheading,
  } = props;

  if (asForm) {
    const { formId, formikProps } = props;
    const nextButtonProps = {
      ...stepFooterProps?.nextButtonProps,
      form: formId,
    };

    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
          <title key="title">{pageTitle || "Literati"}</title>
        </Head>
        <Formik validateOnMount {...formikProps}>
          {(props) => {
            return (
              <Page $bgColor={bgColor} className={`${cls.BASE} ${className}`}>
                <StepHeader {...stepHeaderProps} />
                {prependedContent}
                <ContentSpacing
                  $bgColor={bgColor}
                  className={cls.CONTENT_SPACING}
                  $disableContentSpacing={disableContentSpacing}
                  $footerVariant={footerVariant}
                >
                  <StyledForm className={cls.FORM} id={formId}>
                    {heading ? (
                      <PrimaryHeading className={cls.HEADING}>
                        {heading}
                      </PrimaryHeading>
                    ) : null}
                    {subheading ? (
                      <Subheading className={cls.SUBHEADING}>
                        {subheading}
                      </Subheading>
                    ) : null}
                    {typeof children === "function"
                      ? children(props)
                      : children}
                  </StyledForm>
                  {appendedContent}
                </ContentSpacing>
                {suppressFooter ? null : footerVariant == "sticky" ? (
                  <StepFooter
                    asForm
                    {...{ ...stepFooterProps, nextButtonProps }}
                  />
                ) : (
                  <Footer css="width: 100vw" eventSource="literati-next-kids" />
                )}
              </Page>
            );
          }}
        </Formik>
      </>
    );
  } else {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
          <title key="title">{pageTitle || "Literati"}</title>
        </Head>
        <Page $bgColor={bgColor} className={`${cls.BASE} ${className}`}>
          <StepHeader {...stepHeaderProps} />
          {prependedContent}
          <ContentSpacing
            $bgColor={bgColor}
            className={cls.CONTENT_SPACING}
            $disableContentSpacing={disableContentSpacing}
            $footerVariant={footerVariant}
          >
            {heading ? (
              <PrimaryHeading className={cls.HEADING}>{heading}</PrimaryHeading>
            ) : null}
            {subheading ? (
              <Subheading className={cls.SUBHEADING}>{subheading}</Subheading>
            ) : null}
            {children}
            {appendedContent}
          </ContentSpacing>
          {suppressFooter ? null : footerVariant === "sticky" ? (
            <StepFooter {...stepFooterProps} />
          ) : (
            <Footer css="width: 100vw" eventSource="literati-next-kids" />
          )}
        </Page>
      </>
    );
  }
}

export default StepLayout;
